import { Img, RedactOverlay, useRedacted } from "@/components/common";
import { cn } from "@/lib/utils";
import { Android, BrowserFrame, FullscreenUI, IPhone, Laptop, MacDesktop, ResponsiveSet, Tablet } from "./Frames";
import { Billboard } from "./Billboard";
import { FacebookPost, InstagramPost, LinkedInPost, TikTokPost } from "./Social";
import { Magazine } from "./Magazine";
import { BeforeAfter } from "./BeforeAfter";
import { Gallery, MediaCarousel } from "./Gallery";

const SOCIAL = { social_instagram: InstagramPost, social_facebook: FacebookPost, social_linkedin: LinkedInPost, social_tiktok: TikTokPost };
const SCREENS = { browser: BrowserFrame, mac_desktop: MacDesktop, laptop: Laptop, fullscreen_ui: FullscreenUI };

const Multi = ({ Frame, images, alt, ...rest }) =>
  images.length === 1 ? (
    <Frame src={images[0]} alt={alt} {...rest} />
  ) : (
    <div className="space-y-10">
      <Frame src={images[0]} alt={`${alt} 1`} {...rest} />
      <div className="grid gap-8 md:grid-cols-2">
        {images.slice(1).map((s, i) => <Frame key={s + i} src={s} alt={`${alt} ${i + 2}`} {...rest} />)}
      </div>
    </div>
  );

const Phones = ({ Frame, images, alt }) => (
  <div className="flex flex-wrap items-end justify-center gap-6 sm:flex-nowrap md:gap-12">
    {images.map((s, i) => (
      <div key={s + i} className={cn("w-[70%] sm:w-full sm:max-w-[280px]", i % 2 === 1 && "sm:-translate-y-10")}>
        <Frame src={s} alt={`${alt} — screen ${i + 1}`} />
      </div>
    ))}
  </div>
);

const FullWidth = ({ src, alt }) => {
  const redacted = useRedacted();
  return (
    <div className="relative overflow-hidden bg-surface" style={{ aspectRatio: "21/9" }} data-testid="frame-full-width">
      <Img src={src} alt={alt} sizes="100vw" className="absolute inset-0 h-full w-full object-cover" />
      {redacted && <RedactOverlay />}
    </div>
  );
};

export const renderMedia = (block, alt) => {
  const { type, images = [], meta = {} } = block;
  if (SCREENS[type]) return <Multi Frame={SCREENS[type]} images={images} alt={alt} url={meta.url} />;
  if (SOCIAL[type]) {
    const Post = SOCIAL[type];
    return <Post src={images[0]} alt={alt} caption={block.caption} meta={meta} />;
  }
  switch (type) {
    case "iphone":
      return <Phones Frame={IPhone} images={images} alt={alt} />;
    case "android":
      return <Phones Frame={Android} images={images} alt={alt} />;
    case "tablet":
      return <Multi Frame={Tablet} images={images} alt={alt} landscape={meta.orientation === "landscape"} />;
    case "billboard":
      return <Billboard src={images[0]} alt={alt} />;
    case "magazine":
      return <Magazine images={images} alt={alt} layout={meta.layout} title={block.title} caption={block.caption} />;
    case "before_after":
      return <BeforeAfter before={block.before_image} after={block.after_image} alt={alt} ratio={meta.ratio} />;
    case "gallery":
      return <Gallery images={images} alt={alt} />;
    case "carousel":
      return <MediaCarousel images={images} alt={alt} />;
    case "full_width":
      return <FullWidth src={images[0]} alt={alt} />;
    case "responsive_set":
      return <ResponsiveSet images={images} alt={alt} />;
    default:
      return null;
  }
};

export const TYPE_LABEL = {
  browser: "Browser", mac_desktop: "Desktop", laptop: "Laptop", iphone: "iPhone", android: "Android", tablet: "Tablet",
  fullscreen_ui: "Interface", billboard: "Out-of-home", social_instagram: "Instagram", social_facebook: "Facebook",
  social_linkedin: "LinkedIn", social_tiktok: "TikTok", magazine: "Editorial", before_after: "Before / After",
  gallery: "Gallery", carousel: "Carousel", full_width: "Full bleed", responsive_set: "Responsive",
};

export const MediaBlock = ({ block, projectName, index }) => {
  const alt = block.alt || `${projectName} — ${block.title || TYPE_LABEL[block.type]}`;
  const full = block.type === "full_width";
  const caption = block.type.startsWith("social_") || block.type === "magazine" && block.meta?.layout === "page" ? null : block.caption;
  return (
    <figure data-testid={`media-block-${index}`} data-media-type={block.type} className={cn("py-12 md:py-20", !full && "container-x")}>
      {block.title && !full && (
        <p className="label-mono mb-8 flex items-center gap-3">
          <span className="text-turq">{TYPE_LABEL[block.type]}</span>
          <span className="h-px w-8 bg-line/30" aria-hidden />
          <span>{block.title}</span>
        </p>
      )}
      {renderMedia(block, alt)}
      {caption && <figcaption className={cn("mt-6 max-w-xl text-sm text-silver", full && "container-x")}>{caption}</figcaption>}
    </figure>
  );
};

export const SocialGroup = ({ blocks, projectName, index }) => (
  <figure data-testid={`media-block-${index}`} data-media-type="social_group" className="container-x py-12 md:py-20">
    <p className="label-mono mb-8 flex items-center gap-3">
      <span className="text-turq">Social</span>
      <span className="h-px w-8 bg-line/30" aria-hidden />
      <span>{blocks.map((b) => TYPE_LABEL[b.type]).join(" · ")}</span>
    </p>
    <div className="grid items-start gap-10 md:grid-cols-2 xl:gap-14">
      {blocks.map((b, i) => (
        <div key={i} data-media-type={b.type}>{renderMedia(b, b.alt || `${projectName} — ${TYPE_LABEL[b.type]} post`)}</div>
      ))}
    </div>
  </figure>
);
